
import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Trophy, 
  Search, 
  Award, 
  Star, 
  Users, 
  Calendar,
  TrendingUp,
  BarChart3,
  ThumbsUp,
  FileText,
  AlertCircle,
  Info
} from "lucide-react";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { useLeaderboardData } from "@/hooks/useLeaderboardData";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

// Enum for userLevel titles
enum UserLevel {
  Novice = "Novice",
  Beginner = "Beginner",
  Intermediate = "Intermediate",
  Advanced = "Advanced",
  Expert = "Expert",
  Master = "Master"
}

// Function to get level title based on level number
const getLevelTitle = (level: number): string => {
  if (level < 5) return UserLevel.Novice;
  if (level < 10) return UserLevel.Beginner;
  if (level < 20) return UserLevel.Intermediate;
  if (level < 30) return UserLevel.Advanced;
  if (level < 50) return UserLevel.Expert;
  return UserLevel.Master;
};

// Get level description based on level title
const getLevelDescription = (levelTitle: string): string => {
  switch (levelTitle) {
    case UserLevel.Novice:
      return "Just starting your prompt engineering journey";
    case UserLevel.Beginner:
      return "Building your prompt creation skills";
    case UserLevel.Intermediate:
      return "Consistently creating useful prompts";
    case UserLevel.Advanced:
      return "Creating high-quality prompts with excellent results";
    case UserLevel.Expert:
      return "Mastering the art of prompt engineering";
    case UserLevel.Master:
      return "An exceptional prompt engineer with outstanding contributions";
    default:
      return "Building your skills";
  }
};

// Format date function for better date display
const formatDate = (dateString: string): string => {
  try {
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  } catch (e) {
    return 'Unknown date';
  }
};

const LeaderboardPage = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [timeFrame, setTimeFrame] = useState<"all-time" | "monthly" | "weekly">("all-time");
  
  const { 
    leaderboard, 
    isLoading, 
    userStats, 
    nextLevelPoints
  } = useLeaderboardData(timeFrame);
  
  const filteredUsers = leaderboard.filter(entry => 
    entry.userName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate total community stats
  const totalPrompts = leaderboard.reduce((sum, user) => sum + user.promptsCount, 0);
  const totalLikes = leaderboard.reduce((sum, user) => sum + user.likesReceived, 0);
  const totalMembers = leaderboard.length;
  
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <main className="flex-1 pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-6xl">
          <header className="mb-10">
            <div className="flex items-center gap-2 mb-2">
              <Trophy className="h-8 w-8 text-yellow-500" />
              <h1 className="text-3xl font-bold">Contributor Leaderboard</h1>
            </div>
            <p className="text-muted-foreground">
              Top prompt engineers ranked by their contributions and achievements
            </p>
          </header>

          {/* Community Stats Bar */}
          <div className="bg-primary/5 rounded-lg p-4 mb-8 flex flex-wrap justify-between items-center">
            <div className="flex items-center gap-2 px-4">
              <Users className="h-5 w-5 text-primary" />
              <div>
                <div className="text-sm text-muted-foreground">Community Members</div>
                <div className="font-bold">{totalMembers.toLocaleString()}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 px-4">
              <FileText className="h-5 w-5 text-primary" />
              <div>
                <div className="text-sm text-muted-foreground">Total Prompts</div>
                <div className="font-bold">{totalPrompts.toLocaleString()}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 px-4">
              <ThumbsUp className="h-5 w-5 text-primary" />
              <div>
                <div className="text-sm text-muted-foreground">Total Likes</div>
                <div className="font-bold">{totalLikes.toLocaleString()}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 px-4">
              <TrendingUp className="h-5 w-5 text-primary" />
              <div>
                <div className="text-sm text-muted-foreground">Avg. Points</div>
                <div className="font-bold">
                  {totalMembers > 0 
                    ? Math.round(leaderboard.reduce((sum, user) => sum + user.points, 0) / totalMembers).toLocaleString() 
                    : 0}
                </div>
              </div>
            </div>
          </div>
          
          {user && userStats && (
            <div className="bg-card border border-border rounded-lg p-6 mb-10">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Your Stats</h2>
                <Badge variant="outline" className="flex items-center gap-1">
                  <Info className="h-3 w-3" /> {timeFrame === "all-time" ? "All Time" : timeFrame === "monthly" ? "This Month" : "This Week"}
                </Badge>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="col-span-1">
                  <h3 className="text-md font-medium mb-2">Your Rank</h3>
                  <div className="flex items-center gap-4">
                    <div className="bg-primary/10 p-3 rounded-full">
                      <Trophy className="h-8 w-8 text-primary" />
                    </div>
                    <div>
                      <div className="text-3xl font-bold">#{userStats.rank || '?'}</div>
                      <div className="text-muted-foreground text-sm">out of {totalMembers} contributors</div>
                    </div>
                  </div>
                </div>
                
                <div className="col-span-1">
                  <h3 className="text-md font-medium mb-2">Level {userStats.level}</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Progress to Level {userStats.level + 1}</span>
                      <span className="font-medium">{userStats.points}/{nextLevelPoints}</span>
                    </div>
                    <Progress value={(userStats.points / nextLevelPoints) * 100} />
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium">
                        {getLevelTitle(userStats.level)}
                      </div>
                      <Tooltip content={getLevelDescription(getLevelTitle(userStats.level))}>
                        <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                      </Tooltip>
                    </div>
                  </div>
                </div>
                
                <div className="col-span-1">
                  <h3 className="text-md font-medium mb-2">Your Badges</h3>
                  <div className="flex flex-wrap gap-2">
                    {userStats.badges && userStats.badges.length > 0 ? (
                      userStats.badges.map((badge, index) => (
                        <Badge key={index} variant="outline">{badge}</Badge>
                      ))
                    ) : (
                      <div className="text-muted-foreground">
                        Submit prompts to earn badges!
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
                <div className="bg-background p-4 rounded-lg text-center">
                  <div className="flex justify-center mb-2">
                    <FileText className="h-5 w-5 text-blue-500" />
                  </div>
                  <div className="text-2xl font-bold mb-1">{userStats.promptsCount}</div>
                  <div className="text-sm text-muted-foreground">Prompts</div>
                </div>
                <div className="bg-background p-4 rounded-lg text-center">
                  <div className="flex justify-center mb-2">
                    <ThumbsUp className="h-5 w-5 text-green-500" />
                  </div>
                  <div className="text-2xl font-bold mb-1">{userStats.likesReceived}</div>
                  <div className="text-sm text-muted-foreground">Likes</div>
                </div>
                <div className="bg-background p-4 rounded-lg text-center">
                  <div className="flex justify-center mb-2">
                    <Star className="h-5 w-5 text-yellow-500" />
                  </div>
                  <div className="text-2xl font-bold mb-1">{userStats.points}</div>
                  <div className="text-sm text-muted-foreground">Points</div>
                </div>
                <div className="bg-background p-4 rounded-lg text-center">
                  <div className="flex justify-center mb-2">
                    <Calendar className="h-5 w-5 text-purple-500" />
                  </div>
                  <div className="text-2xl font-bold mb-1">
                    {formatDate(userStats.joinDate)}
                  </div>
                  <div className="text-sm text-muted-foreground">Member Since</div>
                </div>
              </div>
            </div>
          )}
          
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-border overflow-hidden">
            {/* Time Range Selector */}
            <div className="p-6 border-b border-border flex flex-col sm:flex-row justify-between gap-4">
              <Tabs 
                value={timeFrame} 
                onValueChange={(v) => setTimeFrame(v as "all-time" | "monthly" | "weekly")}
                className="w-full sm:w-auto"
              >
                <TabsList>
                  <TabsTrigger value="all-time">All Time</TabsTrigger>
                  <TabsTrigger value="monthly">This Month</TabsTrigger>
                  <TabsTrigger value="weekly">This Week</TabsTrigger>
                </TabsList>
              </Tabs>
              
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input 
                  placeholder="Search users..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            {/* Leaderboard Table */}
            <div className="p-6">
              {isLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin h-8 w-8 border-t-2 border-b-2 border-primary rounded-full"></div>
                </div>
              ) : filteredUsers.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-16">Rank</TableHead>
                        <TableHead>User</TableHead>
                        <TableHead className="text-right">Level</TableHead>
                        <TableHead className="text-right">Points</TableHead>
                        <TableHead className="text-right hidden md:table-cell">Prompts</TableHead>
                        <TableHead className="text-right hidden md:table-cell">Likes</TableHead>
                        <TableHead className="hidden lg:table-cell">Badges</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUsers.map((entry) => (
                        <TableRow 
                          key={entry.id}
                          className={entry.id === user?.id ? "bg-primary/5" : undefined}
                        >
                          <TableCell className="font-medium">
                            {entry.rank === 1 ? (
                              <div className="flex items-center justify-center w-8 h-8 bg-yellow-500/20 rounded-full">
                                <Trophy className="h-4 w-4 text-yellow-500" />
                              </div>
                            ) : entry.rank === 2 ? (
                              <div className="flex items-center justify-center w-8 h-8 bg-gray-400/20 rounded-full">
                                <Trophy className="h-4 w-4 text-gray-400" />
                              </div>
                            ) : entry.rank === 3 ? (
                              <div className="flex items-center justify-center w-8 h-8 bg-amber-600/20 rounded-full">
                                <Trophy className="h-4 w-4 text-amber-600" />
                              </div>
                            ) : (
                              entry.rank
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <Avatar>
                                <AvatarFallback>{entry.userName ? entry.userName[0] : '?'}</AvatarFallback>
                                {entry.avatar && <AvatarImage src={entry.avatar} />}
                              </Avatar>
                              <div>
                                <div className="font-medium">{entry.userName || 'Anonymous User'}</div>
                                <div className="text-xs text-muted-foreground">
                                  {getLevelTitle(entry.level)}
                                </div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">{entry.level}</TableCell>
                          <TableCell className="text-right font-medium">{entry.points}</TableCell>
                          <TableCell className="text-right hidden md:table-cell">{entry.promptsCount}</TableCell>
                          <TableCell className="text-right hidden md:table-cell">{entry.likesReceived}</TableCell>
                          <TableCell className="hidden lg:table-cell">
                            <div className="flex flex-wrap gap-1">
                              {entry.badges && entry.badges.slice(0, 3).map((badge, index) => (
                                <Badge key={index} variant="outline" className="text-xs">{badge}</Badge>
                              ))}
                              {entry.badges && entry.badges.length > 3 && (
                                <Badge variant="outline" className="text-xs">+{entry.badges.length - 3}</Badge>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                  <h3 className="text-xl font-medium mb-2">No users found</h3>
                  <p className="text-muted-foreground">
                    {leaderboard.length === 0 ? 
                      "Could not load leaderboard data. Please try again later." : 
                      "Try adjusting your search"}
                  </p>
                </div>
              )}
            </div>
          </div>
          
          {/* How scoring works section */}
          <div className="mt-10">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <BarChart3 className="h-6 w-6" />
              How Scoring Works
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-primary/10 p-2 rounded-full">
                    <FileText className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg">Creating Prompts</h3>
                </div>
                <p className="text-muted-foreground">
                  Each approved prompt submission earns you 10 points. Higher quality prompts that pass review quickly earn bonus points.
                </p>
              </div>
              
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-primary/10 p-2 rounded-full">
                    <ThumbsUp className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg">Likes & Usage</h3>
                </div>
                <p className="text-muted-foreground">
                  Each like on your prompts earns 2 points. Every time someone uses your prompt, you earn 1 additional point.
                </p>
              </div>
              
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-primary/10 p-2 rounded-full">
                    <TrendingUp className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg">Leveling Up</h3>
                </div>
                <p className="text-muted-foreground">
                  Earn points to level up and unlock new badges and achievements. Higher levels grant additional perks.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

// Create a tooltip component for level descriptions
function Tooltip({ content, children }: { content: string, children: React.ReactNode }) {
  return (
    <div className="relative group">
      {children}
      <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-black text-white text-xs rounded py-1 px-2 min-w-[180px] z-50 pointer-events-none">
        {content}
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-black"></div>
      </div>
    </div>
  );
}

export default LeaderboardPage;
